// Getting started: the setup checklist for a business that just subscribed. On Today it shows how
// far along the shop is and what's next; the full list (with help on every step) is its own screen.
// Steps tick themselves when the store has done them; the owner can also mark a step done or skip it.
import { useState } from 'react';
import { get, post } from '../lib/api';
import { navigate, useLoad } from '../lib/hooks';
import { Badge, Button, Spinner, useAction } from '../ui/kit';
import { Icon } from '../ui/icons';

interface Step { key: string; title: string; why: string; how: string[]; to: string; action: string; done: boolean; found: boolean; skipped: boolean }
interface Onboarding { steps: Step[]; done: number; finished: number; total: number; complete: boolean; dismissed: boolean; tutorials_url: string | null }

function Progress({ data }: { data: Onboarding }) {
  const pct = Math.round((data.finished / Math.max(1, data.total)) * 100);
  return (
    <div className="ob-progress" role="progressbar" aria-valuemin={0} aria-valuemax={data.total} aria-valuenow={data.finished} aria-label={`${data.finished} of ${data.total} setup steps finished`}>
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

/** On Today, until the checklist is finished or put away. */
export function OnboardingCard() {
  const ob = useLoad<Onboarding>(() => get('/api/onboarding'), []);
  const { run, busy } = useAction();
  const d = ob.data;
  if (!d || d.complete || d.dismissed) return null;
  const next = d.steps.filter((s) => !s.done && !s.skipped).slice(0, 3);
  return (
    <section className="card stack ob-card" aria-label="Getting started">
      <div className="spread wrap-sm">
        <div>
          <h2>Getting started</h2>
          <p className="small muted">{d.finished} of {d.total} steps done. Each one ticks itself when your shop does it.</p>
        </div>
        <div className="row-wrap">
          <Button onClick={() => navigate('/getting-started')}>See all steps</Button>
          <Button variant="ghost" busy={busy} onClick={async () => { const r = await run(() => post<Onboarding>('/api/onboarding/dismiss', { dismissed: true })); if (r) ob.setData(r); }}>Hide</Button>
        </div>
      </div>
      <Progress data={d} />
      <div className="list">
        {next.map((s) => (
          <button key={s.key} type="button" className="list-row" onClick={() => navigate(s.to)}>
            <span className="ob-dot" aria-hidden="true" />
            <span className="main-col"><span className="t strong" style={{ display: 'block' }}>{s.title}</span><span className="s" style={{ display: 'block' }}>{s.why}</span></span>
            <Icon.chevronRight />
          </button>
        ))}
      </div>
    </section>
  );
}

export function GettingStartedScreen() {
  const ob = useLoad<Onboarding>(() => get('/api/onboarding'), []);
  const { run, busy } = useAction();
  const [open, setOpen] = useState<string | null>(null);
  const d = ob.data;
  if (ob.error) return <div className="page"><h1>Getting started</h1><div className="banner warn"><Icon.info /><div>{ob.error.message}</div></div></div>;
  if (!d) return <div className="page"><Spinner /></div>;
  const change = async (key: string, body: { done?: boolean; skipped?: boolean }) => {
    const r = await run(() => post<Onboarding>(`/api/onboarding/${key}`, body));
    if (r) ob.setData(r);
  };
  const current = open ?? d.steps.find((s) => !s.done && !s.skipped)?.key ?? null;
  return (
    <div className="page">
      <div className="spread wrap-sm">
        <div>
          <h1>Getting started</h1>
          <p className="muted">{d.complete ? 'Your shop is set up. You can come back here any time.' : `${d.finished} of ${d.total} steps done. Steps tick themselves when your shop does them.`}</p>
        </div>
        <div className="row-wrap">
          {d.tutorials_url ? <a className="btn btn-secondary" href={d.tutorials_url} target="_blank" rel="noreferrer">Watch the tutorials</a> : null}
          {d.dismissed ? <Button variant="ghost" busy={busy} onClick={async () => { const r = await run(() => post<Onboarding>('/api/onboarding/dismiss', { dismissed: false })); if (r) ob.setData(r); }}>Show on Today again</Button> : null}
        </div>
      </div>
      <Progress data={d} />
      <ol className="ob-steps">
        {d.steps.map((s, i) => {
          const expanded = current === s.key;
          return (
            <li key={s.key} className={`card ob-step${s.done ? ' done' : s.skipped ? ' skipped' : ''}`}>
              <button type="button" className="ob-head" aria-expanded={expanded} onClick={() => setOpen(expanded ? '' : s.key)}>
                <span className="ob-num" aria-hidden="true">{s.done ? <Icon.check /> : i + 1}</span>
                <span className="grow">
                  <span className="strong" style={{ display: 'block' }}>{s.title}</span>
                  <span className="small muted" style={{ display: 'block' }}>{s.why}</span>
                </span>
                {s.done ? <Badge tone="green" icon="check">{s.found ? 'Done' : 'Marked done'}</Badge> : s.skipped ? <Badge tone="plain">Skipped</Badge> : null}
              </button>
              {expanded ? (
                <div className="ob-body stack-sm">
                  <ol className="small ob-how">{s.how.map((h, j) => <li key={j}>{h}</li>)}</ol>
                  <div className="row-wrap">
                    <Button variant="primary" onClick={() => navigate(s.to)}>{s.action}</Button>
                    {!s.found && !s.done ? <Button busy={busy} onClick={() => change(s.key, { done: true, skipped: false })}>Mark done</Button> : null}
                    {!s.found && s.done ? <Button variant="ghost" busy={busy} onClick={() => change(s.key, { done: false })}>Not done yet</Button> : null}
                    {!s.done && !s.skipped ? <Button variant="ghost" busy={busy} onClick={() => change(s.key, { skipped: true })}>Skip for now</Button> : null}
                    {s.skipped ? <Button variant="ghost" busy={busy} onClick={() => change(s.key, { skipped: false })}>Don’t skip</Button> : null}
                  </div>
                </div>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
